import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks

async function chunkedUpload(basePath, entityId, file, category, description) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const entityType = basePath.includes('/leads/') ? 'leads' : 'projects';
  const initPath = `/${entityType}/${entityId}/files/chunked/init`;

  // Step 1: Initialize
  const initForm = new FormData();
  initForm.append('filename', file.name);
  initForm.append('file_size', file.size.toString());
  initForm.append('total_chunks', totalChunks.toString());
  initForm.append('category', category || 'other');
  initForm.append('description', description || '');

  const initRes = await api.post(initPath, initForm, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  const { upload_id } = initRes.data;

  // Step 2: Upload chunks sequentially
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);

    const chunkForm = new FormData();
    chunkForm.append('chunk_index', i.toString());
    chunkForm.append('chunk', blob, `chunk_${i}`);

    await api.post(`/${entityType}/${entityId}/files/chunked/${upload_id}`, chunkForm, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  }

  // Step 3: Complete — server processes the file
  const completeRes = await api.post(
    `/${entityType}/${entityId}/files/chunked/${upload_id}/complete`,
    {},
    { timeout: 300000 }
  );
  return completeRes;
}

// Auth APIs
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  googleSignIn: () => api.get('/auth/google/signin'),
  updateCompanyName: (company) => api.put('/auth/me/company', { company }),
};

// Lead APIs
export const leadApi = {
  getAll: (stage) => api.get('/leads', { params: { stage } }),
  getOne: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  updateStage: (id, stage) => api.put(`/leads/${id}/stage?stage=${stage}`),
  assign: (id, managerId) => api.put(`/leads/${id}/assign`, { manager_id: managerId }),
  getFollowUpHours: () => api.get('/leads/follow-up-hours'),
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/leads/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // File management (S3-backed)
  getFiles: (leadId, category) => api.get(`/leads/${leadId}/files`, { params: { category } }),
  uploadFile: (leadId, file, category, description) => {
    // Use chunked upload for files > 5MB
    if (file.size > 5 * 1024 * 1024) {
      return chunkedUpload(`/leads/${leadId}/files`, leadId, file, category, description);
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category || 'other');
    formData.append('description', description || '');
    return api.post(`/leads/${leadId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  deleteFile: (leadId, fileId) => api.delete(`/leads/${leadId}/files/${fileId}`),
  getFileUrl: (leadId, fileId) => api.get(`/leads/${leadId}/files/${fileId}/download-url`),
};

// Activity APIs
export const activityApi = {
  getAll: (params) => api.get('/activities', { params }),
  create: (data) => api.post('/activities', data),
};

// Reminder APIs
export const reminderApi = {
  getAll: (params) => api.get('/reminders', { params }),
  create: (data) => api.post('/reminders', data),
  complete: (id) => api.put(`/reminders/${id}/complete`),
  delete: (id) => api.delete(`/reminders/${id}`),
};

// Calendar Event APIs
export const calendarApi = {
  getEvents: (params) => api.get('/calendar/events', { params }),
  createEvent: (data) => api.post('/calendar/events', data),
  updateEvent: (id, data) => api.put(`/calendar/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
};

// Project APIs
export const projectApi = {
  getAll: (status) => api.get('/projects', { params: { status } }),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getEstimatesForTasks: (projectId) => api.get(`/projects/${projectId}/estimates-for-tasks`),
  importTasksFromEstimate: (projectId, estimateId) =>
    api.post(`/projects/${projectId}/tasks/import-from-estimate/${estimateId}`),
  updatePhase: (id, data) => api.put(`/projects/${id}/phase`, data),
  addTask: (projectId, phaseName, data) => api.post(`/projects/${projectId}/phases/${phaseName}/tasks`, data),
  updateTask: (projectId, phaseName, taskId, data) => api.put(`/projects/${projectId}/phases/${phaseName}/tasks/${taskId}`, data),
  reorderTasks: (projectId, phaseName, taskIds) => api.put(`/projects/${projectId}/phases/${phaseName}/tasks/reorder`, { task_ids: taskIds }),
  toggleSubtask: (projectId, phaseName, taskId, subtaskId, completed) => api.put(`/projects/${projectId}/phases/${phaseName}/tasks/${taskId}/subtasks/${subtaskId}`, null, { params: { completed } }),
  addTaskPhoto: (projectId, phaseName, taskId, data) => api.post(`/projects/${projectId}/phases/${phaseName}/tasks/${taskId}/photos`, data),
  deleteTaskPhoto: (projectId, phaseName, taskId, photoId) => api.delete(`/projects/${projectId}/phases/${phaseName}/tasks/${taskId}/photos/${photoId}`),
  verifyTask: (projectId, phaseName, taskId, verified) => api.put(`/projects/${projectId}/phases/${phaseName}/tasks/${taskId}/verify`, null, { params: { verified } }),
  deleteTask: (projectId, phaseName, taskId) => api.delete(`/projects/${projectId}/phases/${phaseName}/tasks/${taskId}`),
  sendTasks: (projectId, email) => api.post(`/projects/${projectId}/send-tasks?email=${email}`),
  assign: (id, managerId) => api.put(`/projects/${id}/assign`, { manager_id: managerId }),
  getAssignedManager: (id) => api.get(`/projects/${id}/assigned-manager`),
  getBillingSummary: (id) => api.get(`/projects/${id}/billing-summary`),
  // S3 file management (unified with leads)
  getFiles: (projectId, category) => api.get(`/projects/${projectId}/files`, { params: { category } }),
  uploadFile: (projectId, file, category, description) => {
    // Use chunked upload for files > 5MB
    if (file.size > 5 * 1024 * 1024) {
      return chunkedUpload(`/projects/${projectId}/files`, projectId, file, category, description);
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category || 'other');
    formData.append('description', description || '');
    return api.post(`/projects/${projectId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  deleteFile: (projectId, fileId) => api.delete(`/projects/${projectId}/files/${fileId}`),
  getFileUrl: (projectId, fileId) => api.get(`/projects/${projectId}/files/${fileId}/download-url`),
  getOriginalUrl: (projectId, fileId) => api.get(`/projects/${projectId}/files/${fileId}/original-url`),
};

// Photo APIs
export const photoApi = {
  getAll: (projectId) => api.get('/photos', { params: { project_id: projectId } }),
  upload: (data) => api.post('/photos', data),
  delete: (id) => api.delete(`/photos/${id}`),
};

// Document APIs
export const documentApi = {
  getAll: (projectId, documentType) => api.get('/documents', { params: { project_id: projectId, document_type: documentType } }),
  upload: (data) => api.post('/documents', data),
  delete: (id) => api.delete(`/documents/${id}`),
  getUrl: (id) => `${API_URL}/documents/${id}/file`,
};

// Team APIs
export const teamApi = {
  getAll: () => api.get('/team'),
  getMembers: () => api.get('/team'),  // Alias for getAll
  invite: (data) => api.post('/team/invite', data),
  assignProject: (memberId, projectId) => api.put(`/team/${memberId}/assign-project?project_id=${projectId}`),
  remove: (memberId) => api.delete(`/team/${memberId}`),
  resetPassword: (memberId) => api.post(`/team/${memberId}/reset-password`),
  getMemberDashboard: (memberId) => api.get(`/team/${memberId}/dashboard`),
};

// Subcontractor APIs
export const subcontractorApi = {
  getAll: (type) => api.get('/subcontractors', { params: { type } }),
  getOne: (id) => api.get(`/subcontractors/${id}`),
  create: (data) => api.post('/subcontractors', data),
  update: (id, data) => api.put(`/subcontractors/${id}`, data),
  delete: (id) => api.delete(`/subcontractors/${id}`),
  getTypes: () => api.get('/subcontractor-types'),
};

// Estimate APIs
export const estimateApi = {
  importForLead: (leadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/estimates/leads/${leadId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  importForProject: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/estimates/projects/${projectId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  getForLead: (leadId) => api.get(`/estimates/leads/${leadId}`),
  getForProject: (projectId) => api.get(`/estimates/projects/${projectId}`),
  getForClient: (clientId) => api.get(`/estimates/clients/${clientId}`),
  getOne: (estimateId) => api.get(`/estimates/${estimateId}`),
  getPdf: (estimateId, internal = false) => api.get(`/estimates/${estimateId}/pdf`, { params: { internal }, responseType: 'blob' }),
  update: (estimateId, data) => api.put(`/estimates/${estimateId}`, data),
  createManualForLead: (leadId, data) => api.post(`/estimates/leads/${leadId}/manual`, data),
  createManualForProject: (projectId, data) => api.post(`/estimates/projects/${projectId}/manual`, data),
  updateManual: (estimateId, data) => api.put(`/estimates/${estimateId}/manual`, data),
  delete: (estimateId) => api.delete(`/estimates/${estimateId}`),
  sell: (estimateId) => api.post(`/estimates/${estimateId}/sell`),
};

// Expense APIs
export const expenseApi = {
  getForProject: (projectId, category) => api.get(`/expenses/projects/${projectId}`, { params: { category } }),
  scanReceipt: (file) => {
    const formData = new FormData();
    formData.append('receipt', file);
    return api.post('/expenses/scan-receipt', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    });
  },
  create: (projectId, data) => {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('amount', data.amount.toString());
    formData.append('category', data.category || 'materials');
    if (data.vendor) formData.append('vendor', data.vendor);
    if (data.date) formData.append('date', data.date);
    if (data.notes) formData.append('notes', data.notes);
    if (data.scope_type) formData.append('scope_type', data.scope_type);
    if (data.scope_id) formData.append('scope_id', data.scope_id);
    if (data.scope_label) formData.append('scope_label', data.scope_label);
    if (data.subcontractor_id) formData.append('subcontractor_id', data.subcontractor_id);
    if (data.schedule_c_category) formData.append('schedule_c_category', data.schedule_c_category);
    if (data.receipt) formData.append('receipt', data.receipt);
    return api.post(`/expenses/projects/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  update: (expenseId, data) => {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('amount', data.amount.toString());
    formData.append('category', data.category || 'materials');
    formData.append('vendor', data.vendor || '');
    formData.append('date', data.date || '');
    formData.append('notes', data.notes || '');
    formData.append('scope_type', data.scope_type || '');
    formData.append('scope_id', data.scope_id || '');
    formData.append('scope_label', data.scope_label || '');
    formData.append('subcontractor_id', data.subcontractor_id || '');
    formData.append('schedule_c_category', data.schedule_c_category || '');
    if (data.remove_receipt) formData.append('remove_receipt', 'true');
    if (data.receipt) formData.append('receipt', data.receipt);
    return api.put(`/expenses/${expenseId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  delete: (expenseId) => api.delete(`/expenses/${expenseId}`),
  getBudget: (projectId) => api.get(`/expenses/projects/${projectId}/budget`),
};

// Change Order / T&M APIs
export const changeOrderApi = {
  getForProject: (projectId) => api.get(`/change-orders/projects/${projectId}`),
  getOne: (id) => api.get(`/change-orders/${id}`),
  create: (projectId, data) => api.post(`/change-orders/projects/${projectId}`, data),
  update: (id, data) => api.put(`/change-orders/${id}`, data),
  approve: (id) => api.post(`/change-orders/${id}/approve`),
  reopen: (id) => api.post(`/change-orders/${id}/reopen`),
  remove: (id) => api.delete(`/change-orders/${id}`),
};

// Client APIs
export const clientApi = {
  getAll: (search) => api.get('/clients', { params: { search } }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  search: (q) => api.get('/clients/search', { params: { q } }),
  lookupByPhone: (phone) => api.get('/clients/lookup-by-phone', { params: { phone } }),
  getHistory: (id) => api.get(`/clients/${id}/history`),
  getStatementPdf: (id) => api.get(`/clients/${id}/statement/pdf`, { responseType: 'blob' }),
  sendStatement: (id) => api.post(`/clients/${id}/statement/send`),
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/clients/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Billing APIs
export const billingApi = {
  getSubscription: () => api.get('/billing/subscription'),
  getPlans: () => api.get('/billing/plans'),
  createCheckout: (data) => api.post('/billing/checkout', data),
  getCheckoutStatus: (sessionId) => api.get(`/billing/checkout/status/${sessionId}`),
};

// Dashboard APIs
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Reports APIs
export const reportsApi = {
  pipelineFunnel: (period) => api.get('/reports/pipeline-funnel', { params: { period } }),
  avgTimeInStage: (period) => api.get('/reports/avg-time-in-stage', { params: { period } }),
  wonVsLost: (period) => api.get('/reports/won-vs-lost', { params: { period } }),
  revenueByMonth: (period) => api.get('/reports/revenue-by-month', { params: { period } }),
  pipelineValueByStage: (period) => api.get('/reports/pipeline-value-by-stage', { params: { period } }),
  avgDealSize: (period) => api.get('/reports/avg-deal-size', { params: { period } }),
  leadsBySource: (period) => api.get('/reports/leads-by-source', { params: { period } }),
  leadVolume: (period) => api.get('/reports/lead-volume', { params: { period } }),
  projectsByPhase: () => api.get('/reports/projects-by-phase'),
  projectStatusSummary: () => api.get('/reports/project-status-summary'),
  getFavorites: () => api.get('/reports/favorites'),
  updateFavorites: (favorites) => api.put('/reports/favorites', { favorites }),
};

// Settings APIs
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updateProfile: (data) => api.put('/settings/profile', null, { params: data }),
  getGoogleAuthUrl: () => api.get('/auth/google/login'),
  disconnectGoogle: () => api.post('/auth/google/disconnect'),
  changePassword: (data) => api.post('/auth/change-password', data),
  // Branding APIs
  getBranding: () => api.get('/settings/branding'),
  updateBranding: (data) => api.put('/settings/branding', data),
  deleteLogo: () => api.delete('/settings/branding/logo'),
  getColorSchemes: () => api.get('/settings/color-schemes'),
  getEstimateDefaults: () => api.get('/settings/estimate-defaults'),
  updateEstimateDefaults: (data) => api.put('/settings/estimate-defaults', data),
};

export const accountingApi = {
  getStatus: () => api.get('/accounting/status'),
  enable: (enabled) => api.post('/accounting/enable', null, { params: { enabled } }),
  updateSettings: (setaside_pct) => api.put('/accounting/settings', null, { params: { setaside_pct } }),
  getSummary: (year) => api.get('/accounting/summary', { params: { year } }),
  getMileage: (year, projectId) => api.get('/accounting/mileage', { params: { year, project_id: projectId } }),
  addMileage: (data) => api.post('/accounting/mileage', data),
  deleteMileage: (id) => api.delete(`/accounting/mileage/${id}`),
  get1099Report: (year) => api.get('/accounting/1099-report', { params: { year } }),
  download1099Csv: (year) => api.get('/accounting/1099-report/csv', { params: { year }, responseType: 'blob' }),
  getBusinessExpenses: (year) => api.get('/accounting/business-expenses', { params: { year } }),
  addBusinessExpense: (data) => {
    const fd = new FormData();
    fd.append('description', data.description);
    fd.append('amount', String(data.amount));
    fd.append('date', data.date || '');
    fd.append('schedule_c_category', data.schedule_c_category || 'Other Business Expenses');
    fd.append('vendor', data.vendor || '');
    fd.append('notes', data.notes || '');
    if (data.receipt) fd.append('receipt', data.receipt);
    return api.post('/accounting/business-expenses', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
  },
  deleteBusinessExpense: (id) => api.delete(`/accounting/business-expenses/${id}`),
  updateSubTax: (subId, data) => {
    const fd = new FormData();
    fd.append('tax_id', data.tax_id || '');
    fd.append('w9_on_file', data.w9_on_file ? 'true' : 'false');
    return api.put(`/accounting/subcontractors/${subId}/tax`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadW9: (subId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/accounting/subcontractors/${subId}/w9`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Portal API (public)
export const portalApi = {
  getProject: (shareToken) => axios.get(`${API_URL}/portal/${shareToken}`),
  addTask: (shareToken, phaseName, name) => axios.post(`${API_URL}/portal/${shareToken}/phases/${encodeURIComponent(phaseName)}/tasks`, { name }),
  addNote: (shareToken, phaseName, taskId, note) => axios.post(`${API_URL}/portal/${shareToken}/phases/${encodeURIComponent(phaseName)}/tasks/${taskId}/note`, { note }),
};

// Admin API (requires admin secret)
export const adminApi = {
  verify: (adminSecret) => axios.post(`${API_URL}/admin/verify?admin_secret=${adminSecret}`),
  getStats: (adminSecret) => axios.get(`${API_URL}/admin/stats?admin_secret=${adminSecret}`),
  getUsers: (adminSecret, search = '') => axios.get(`${API_URL}/admin/users?admin_secret=${adminSecret}${search ? `&search=${search}` : ''}`),
  listGrants: (adminSecret) => axios.get(`${API_URL}/admin/list-grants?admin_secret=${adminSecret}`),
  grantSubscription: (data) => axios.post(`${API_URL}/admin/grant-free-subscription`, data),
  revokeGrant: (userEmail, adminSecret) => axios.post(`${API_URL}/admin/revoke-grant?user_email=${userEmail}&admin_secret=${adminSecret}`),
  resetPassword: (data) => axios.post(`${API_URL}/admin/reset-password`, data),
  suspendAccount: (userEmail, adminSecret, reason = '') => axios.post(`${API_URL}/admin/suspend-account?user_email=${userEmail}&admin_secret=${adminSecret}&reason=${encodeURIComponent(reason)}`),
  reactivateAccount: (userEmail, adminSecret) => axios.post(`${API_URL}/admin/reactivate-account?user_email=${userEmail}&admin_secret=${adminSecret}`),
  cancelSubscription: (userEmail, adminSecret) => axios.post(`${API_URL}/admin/cancel-subscription?user_email=${userEmail}&admin_secret=${adminSecret}`),
  deleteUser: (userEmail, adminSecret) => axios.delete(`${API_URL}/admin/delete-user?user_email=${userEmail}&admin_secret=${adminSecret}`),
  // Platform settings
  getPlatformSettings: (adminSecret) => axios.get(`${API_URL}/admin/platform-settings?admin_secret=${adminSecret}`),
  updatePlatformDomain: (domain, adminSecret) => axios.put(`${API_URL}/admin/platform-settings/domain?domain=${encodeURIComponent(domain)}&admin_secret=${adminSecret}`),
  // Bug tracker
  getBugTrackerSettings: (adminSecret) => axios.get(`${API_URL}/admin/bug-tracker/settings?admin_secret=${adminSecret}`),
  updateBugTrackerSettings: (settings, adminSecret) => axios.put(`${API_URL}/admin/bug-tracker/settings?admin_secret=${adminSecret}`, settings),
  getBugReports: (adminSecret, filters = {}) => {
    const params = new URLSearchParams({ admin_secret: adminSecret });
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    return axios.get(`${API_URL}/admin/bug-tracker/reports?${params}`);
  },
  updateBugReport: (reportId, data, adminSecret) => axios.put(`${API_URL}/admin/bug-tracker/reports/${reportId}?admin_secret=${adminSecret}`, data),
  deleteBugReport: (reportId, adminSecret) => axios.delete(`${API_URL}/admin/bug-tracker/reports/${reportId}?admin_secret=${adminSecret}`),
  // Admin Google
  getAdminGoogleStatus: (adminSecret) => axios.get(`${API_URL}/admin/google/status?admin_secret=${adminSecret}`),
  getAdminGoogleAuthUrl: (adminSecret) => axios.get(`${API_URL}/admin/google/auth-url?admin_secret=${adminSecret}`),
  adminGoogleCallback: (code, adminSecret) => axios.post(`${API_URL}/admin/google/callback?code=${encodeURIComponent(code)}&admin_secret=${adminSecret}`),
  disconnectAdminGoogle: (adminSecret) => axios.post(`${API_URL}/admin/google/disconnect?admin_secret=${adminSecret}`),
  // Contact Messages
  getContactMessages: (adminSecret, filters = {}) => {
    const params = new URLSearchParams({ admin_secret: adminSecret });
    if (filters.status) params.append('status', filters.status);
    return axios.get(`${API_URL}/admin/contact-messages?${params}`);
  },
  updateContactMessage: (messageId, data, adminSecret) => axios.put(`${API_URL}/admin/contact-messages/${messageId}?admin_secret=${adminSecret}`, data),
  deleteContactMessage: (messageId, adminSecret) => axios.delete(`${API_URL}/admin/contact-messages/${messageId}?admin_secret=${adminSecret}`),
  // Lead follow-up settings
  getLeadFollowUpHours: (adminSecret) => axios.get(`${API_URL}/admin/lead-settings/follow-up-hours?admin_secret=${adminSecret}`),
  updateLeadFollowUpHours: (hours, adminSecret) => axios.put(`${API_URL}/admin/lead-settings/follow-up-hours?hours=${hours}&admin_secret=${adminSecret}`),
};

// Bug Report API (for users)
export const bugApi = {
  getSettings: () => api.get('/bugs/settings'),
  submit: (data) => api.post('/bugs', data),
};

// Contact Form API (public)
export const contactApi = {
  submit: (data) => axios.post(`${API_URL}/contact`, data),
};

// Password Reset APIs (public)
export const passwordResetApi = {
  forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot-password`, { email }),
  verifyToken: (token) => axios.get(`${API_URL}/auth/verify-reset-token?token=${token}`),
  resetPassword: (token, newPassword) => axios.post(`${API_URL}/auth/reset-password`, { token, new_password: newPassword }),
};

// Lead Form APIs
export const leadFormApi = {
  getSettings: () => api.get('/settings/lead-form'),
  updateSettings: (settings) => api.put('/settings/lead-form', settings),
  regenerateToken: () => api.post('/settings/lead-form/regenerate-token'),
  updateDomain: (domain) => api.put('/settings/lead-form/domain', null, { params: { domain } }),
  // Public endpoints
  getPublicForm: (token) => axios.get(`${API_URL}/lead-form/${token}`),
  submitForm: (token, data) => axios.post(`${API_URL}/lead-form/${token}/submit`, data),
};

// Testimonials APIs
export const testimonialsApi = {
  // Public
  submit: (data) => axios.post(`${API_URL}/testimonials/submit`, data),
  getPublished: () => axios.get(`${API_URL}/testimonials/published`),
  // Admin
  getAll: (adminSecret, status) => axios.get(`${API_URL}/testimonials/admin/all?admin_secret=${adminSecret}${status ? `&status=${status}` : ''}`),
  update: (id, data, adminSecret) => axios.put(`${API_URL}/testimonials/admin/${id}?admin_secret=${adminSecret}`, data),
  delete: (id, adminSecret) => axios.delete(`${API_URL}/testimonials/admin/${id}?admin_secret=${adminSecret}`),
};

// Beta Signup APIs
export const betaApi = {
  // Public
  signup: (data) => axios.post(`${API_URL}/beta/signup`, data),
  // Admin
  getSignups: (adminSecret, status) => axios.get(`${API_URL}/beta/admin/signups?admin_secret=${adminSecret}${status ? `&status=${status}` : ''}`),
  updateSignup: (id, data, adminSecret) => axios.put(`${API_URL}/beta/admin/signups/${id}?admin_secret=${adminSecret}`, data),
  deleteSignup: (id, adminSecret) => axios.delete(`${API_URL}/beta/admin/signups/${id}?admin_secret=${adminSecret}`),
  exportSignups: (adminSecret) => axios.get(`${API_URL}/beta/admin/export?admin_secret=${adminSecret}`),
  approveSignup: (id, adminSecret) => axios.post(`${API_URL}/beta/admin/signups/${id}/approve?admin_secret=${adminSecret}`),
};

// Push Notification APIs
export const notificationApi = {
  getVapidKey: () => api.get('/notifications/push/vapid-key'),
  subscribe: (subscription) => api.post('/notifications/push/subscribe', subscription),
  unsubscribe: (endpoint) => api.delete('/notifications/push/unsubscribe', { params: { endpoint } }),
  getStatus: () => api.get('/notifications/push/status'),
};

// Invoice APIs
export const invoiceApi = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  permanentDelete: (id) => api.delete(`/invoices/${id}/permanent`),
  send: (id, copyToSelf) => api.post(`/invoices/${id}/send`, null, copyToSelf === undefined ? undefined : { params: { copy_to_self: copyToSelf } }),
  finalize: (id) => api.post(`/invoices/${id}/finalize`),
  markPaid: (id, amount, method, note) => api.post(`/invoices/${id}/mark-paid`, null, { params: { amount, method, note } }),
  deletePayment: (id, paymentId) => api.delete(`/invoices/${id}/payments/${paymentId}`),
  duplicate: (id) => api.post(`/invoices/${id}/duplicate`),
  getPdf: (id, internal = false) => api.get(`/invoices/${id}/pdf`, { params: { internal }, responseType: 'blob' }),
  getNextNumber: () => api.get('/invoices/next-number'),
  getSettings: () => api.get('/invoices/settings'),
  updateSettings: (data) => api.put('/invoices/settings', data),
  exportCsv: () => api.get('/invoices/export/csv', { responseType: 'blob' }),
};

// Time & Activity Tracker APIs (Solo Builder bundle)
export const timeApi = {
  getSettings: () => api.get('/time/settings'),
  updateSettings: (rate) => api.put('/time/settings', null, { params: { default_hourly_rate: rate } }),
  getEntries: (params) => api.get('/time', { params }),
  getSummary: (year) => api.get('/time/summary', { params: { year } }),
  getUnbilled: (params) => api.get('/time/unbilled', { params }),
  create: (data) => api.post('/time', data),
  update: (id, data) => api.put(`/time/${id}`, data),
  delete: (id) => api.delete(`/time/${id}`),
  markBilled: (entryIds, invoiceId) => api.post('/time/mark-billed', { entry_ids: entryIds, invoice_id: invoiceId }),
  laborVsEstimate: (projectId) => api.get(`/time/labor-vs-estimate/${projectId}`),
};

// Credit Memo APIs
export const creditMemoApi = {
  getAll: (params) => api.get('/credit-memos', { params }),
  getOne: (id) => api.get(`/credit-memos/${id}`),
  create: (data) => api.post('/credit-memos', data),
  delete: (id) => api.delete(`/credit-memos/${id}`),
  send: (id) => api.post(`/credit-memos/${id}/send`),
  getPdf: (id) => api.get(`/credit-memos/${id}/pdf`, { responseType: 'blob' }),
  getNextNumber: () => api.get('/credit-memos/next-number'),
};

export const stripeConnectApi = {
  getStatus: () => api.get('/stripe-connect/status'),
  connect: (api_key) => api.post('/stripe-connect/connect', { api_key }),
  disconnect: () => api.post('/stripe-connect/disconnect'),
  getInvoicePaymentLink: (invoiceId) => api.get(`/stripe-connect/invoices/${invoiceId}/payment-link`),
};

export default api;
